import { cx } from "@emotion/css";
import { ArrowLeft, ChevronDown, ChevronUp, List } from "lucide-react";
import { type MouseEvent, type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { Article } from "src/api/articles";
import Image from "src/components/Default/Image";
import { GreyButton } from "src/components/Default/LekUI/Button";
import { Menu } from "src/components/Default/LekUI/Menu";
import { TiptapContent } from "src/components/Default/TiptapContent";

type ArticleViewBack =
	// onClick przy linku: zwykły klik zamiast nawigacji (np. history.goBack), ctrl/⌘-klik dalej otwiera `to`
	| { to: string; label: string; onClick?: () => void }
	| { onClick: () => void; label: string };

export interface ArticleViewProps {
	article: Pick<Article, "title" | "content" | "source"> &
		Partial<Pick<Article, "categories">>;
	back: ArticleViewBack;
	/** Pod tytułem, np. status i tagi wpisu bloga. */
	subtitle?: ReactNode;
	/** Po prawej od spisu treści, np. akcje admina. */
	actions?: ReactNode;
	className?: string;
}

/** Artykuł jako jeden dokument: spis treści z h2 + cały HTML z /v2. Wspólny dla panelu i admina. */
export function ArticleView({
	article,
	back,
	subtitle,
	actions,
	className,
}: ArticleViewProps) {
	const { t } = useTranslation();
	const { html, toc } = useMemo(
		() => withSectionIds(article.content ?? ""),
		[article.content],
	);
	const department = article.categories?.[0]?.department;
	const [lightbox, setLightbox] = useState<{
		images: { image: string }[];
		index: number;
	} | null>(null);

	// ponytail: delegowany klik jak w GuideBlock, tresc to statyczny HTML
	const openImage = (e: MouseEvent<HTMLDivElement>) => {
		const target = e.target as HTMLElement;
		if (target.tagName !== "IMG") return;
		const imgs = Array.from(e.currentTarget.querySelectorAll("img"));
		setLightbox({
			images: imgs.map((img) => ({ image: img.src })),
			index: imgs.indexOf(target as HTMLImageElement),
		});
	};

	return (
		<div className={cx("article-view", className)}>
			<div className="article-view--card">
				<div className="article-view--header">
					{"to" in back ? (
						<Link
							to={back.to}
							className="article-view--back"
							onClick={(e) => {
								if (!back.onClick || e.metaKey || e.ctrlKey) return;
								e.preventDefault();
								back.onClick();
							}}
						>
							<ArrowLeft className="icon-small" />
							{back.label}
							{department && ` / ${department}`}
						</Link>
					) : (
						<button
							type="button"
							onClick={back.onClick}
							className="article-view--back"
						>
							<ArrowLeft className="icon-small" />
							{back.label}
						</button>
					)}
					{/* breadcrumb na całą szerokość, spis treści i akcje w wierszu tytułu */}
					<div className="flex items-start justify-between flex-wrap gap-16">
						<div className="flex flex-col gap-12 article-view--title-block">
							<h1 className="article-view--title">{article.title}</h1>
							{subtitle}
						</div>
						<div className="flex items-center gap-8">
							{toc.length > 0 && (
								<Menu
									closeAfterClick
									menuPopperClassName="article-view--toc"
									renderTrigger={({ onClick, anchor }) => (
										<GreyButton
											data-test-id="article-toc-button"
											onClick={onClick}
											startIcon={<List className="icon-small" />}
											endIcon={
												anchor ? (
													<ChevronUp className="icon-small" />
												) : (
													<ChevronDown className="icon-small" />
												)
											}
										>
											{t("articles.contents", "Spis treści")}
											<span className="article-view--toc-count overwritten-color-to-black100">
												{toc.length}
											</span>
										</GreyButton>
									)}
									items={toc.map((section, i) => ({
										Icon: (
											<span className="article-view--toc-num">{i + 1}</span>
										),
										title: section.label,
										// długie nazwy sekcji zawijają się zamiast ucinać
										itemTextClassName: "ws-normal",
										onClick: () =>
											document.getElementById(section.id)?.scrollIntoView({
												behavior: "smooth",
												block: "start",
											}),
									}))}
								/>
							)}
							{actions}
						</div>
					</div>
				</div>
				<TiptapContent
					html={html}
					className="article-view--content"
					onClick={openImage}
				/>
				{article.source && (
					<div className="article-view--source">
						<span className="article-view--source-title">
							{t("exam.source")}
						</span>
						<TiptapContent
							html={article.source}
							className="article-view--content"
						/>
					</div>
				)}
			</div>
			{lightbox && (
				<Image
					images={lightbox.images}
					index={lightbox.index}
					close={() => setLightbox(null)}
				/>
			)}
		</div>
	);
}

// h2 = sekcje spisu treści (kotwice do scrolla)
function withSectionIds(content: string) {
	const doc = new DOMParser().parseFromString(content, "text/html");
	const toc = Array.from(doc.querySelectorAll("h2")).map((h2, i) => {
		h2.id = `section-${i}`;
		return { id: h2.id, label: h2.textContent?.trim() ?? "" };
	});
	return { html: doc.body.innerHTML, toc };
}
